import React, { useEffect, useState } from 'react';
import { Button, Row, Col, Card, Container, OverlayTrigger, Popover, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useGetQuery, usePostQuery } from '../../../Utils/api';
import { useParams } from 'react-router-dom';
import { Entente, Lieu, ModeleCourriel } from '../../../../../common/src/entities';
import { EmailAttachment } from '../../../../../common/src/interfaces/EmailAttachment';
import { BiChevronUp, BiChevronDown, BiAt, BiNote, BiPhone } from 'react-icons/bi';
import ContactEntente from '../../../../../common/src/entities/contact-entente.entity';
import { LinkContainer } from 'react-router-bootstrap';
import RichTextEditor from '../../Common/RichTextEditor';
import { useAlertDialog } from '../../../Utils/dialogContext';
import { beautifyType } from './PDF/CommonHeaderPDF';
import { fetchActivitesEntentePdfBlob, mergePDFs } from './PDF/LoadEntentePDF';
import EntentePDF from './PDF/EntentePDF';
import { pdf } from '@react-pdf/renderer';
import LegendeCourriel from '../ModelesCourriel/LegendeCourriel';

const MenuEntentes: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { data: entente } = useGetQuery<Entente>([`ententes/${id}`]);
    const { data: modelesEmails } = useGetQuery<ModeleCourriel[]>(`modele-courriel`);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<ModeleCourriel | null>(null);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
    const [emailAttachments, setEmailAttachments] = useState<string[]>([]);
    const [envoyerEmail] = usePostQuery(`ententes/${id}/courriel`);
    const bsAlert = useAlertDialog();
    const [emailLoading, setEmailLoading] = useState(false);

    const documentTypes = ['soumission', 'reservation', 'bonDeCommande', 'facture', 'etatDeCompte'];

    if (entente) {
        entente.contacts = entente.contacts || ((entente.client?.contacts as unknown) as ContactEntente[]) || [];
        entente.roles = entente.roles || entente.client?.roles || ';;;;';
    }

    const formatDate = (dateString?: string): string => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const formatAddress = (adresse: Lieu | undefined): string => {
        if (!adresse) return 'N/A';
        return `${adresse.adresse}, ${adresse.ville}, ${adresse.province}, ${adresse.code_postal}`;
    };

    const roleLabels = [
        '1. Planification d’activité',
        '2. Approbation d’activité',
        '3. Organisation de parascolaires',
        '4. Facturation',
    ];

    const getContactsByRole = (roleIndex: number) => {
        if (!entente?.roles || !entente?.contacts) return 'N/A';

        const roleGroups = entente.roles.split(';');
        if (!roleGroups[roleIndex]) return 'N/A';

        const contactIds = roleGroups[roleIndex].split(',').map((id) => id.trim());
        const contacts = entente.contacts.filter((c) => contactIds.includes(String(c.contact.id)));

        return contacts.length > 0
            ? contacts.map((c, index) => (
                  <span key={index} className="d-inline-block">
                      {c.contact.name}
                      {index !== contacts.length - 1 ? ', ' : ''}
                  </span>
              ))
            : 'N/A';
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('emailModal') === 'true') {
            setShowEmailModal(true);
        }
    }, [location.search]);

    const handleTemplateChange = (templateId: string) => {
        const template = modelesEmails?.find((t) => String(t.id) === templateId) || null;
        setSelectedTemplate(template);
        if (template) {
            setEmailSubject(template.title);
            setEmailMessage(template.body);
        } else {
            setEmailSubject('');
            setEmailMessage('');
        }
    };

    async function blobToBase64(blob: Blob): Promise<string | ArrayBuffer | null> {
        return new Promise((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    }

    const createEmailAttachmentData = async (docType: string) => {
        const ententeInstance = pdf(<EntentePDF entente={entente as Entente} type={docType} />);
        const ententeBlob = await ententeInstance.toBlob();

        const activiteBlobs =
            docType === 'soumission' ? await fetchActivitesEntentePdfBlob(entente as Entente, true) : [];

        const mergedBlob = await mergePDFs([ententeBlob, ...activiteBlobs]);

        const pdfFilename = `Entente - ${entente?.name} - ${beautifyType(docType)}.pdf`;

        return {
            type: docType,
            filename: pdfFilename,
            content: (await blobToBase64(mergedBlob)) as string,
        } as EmailAttachment;
    };

    const handleAddEmailAttachment = async (docType) => {
        if (!emailAttachments.includes(docType)) {
            setEmailAttachments((prev) => [...prev, docType]);
        }
    };

    const handleRemoveEmailAttachment = (docType) => {
        setEmailAttachments((prev) => prev.filter((item) => item !== docType));
    };

    const replaceEmailVariables = (message: string) => {
        return message
            .replace(/\[\[CLIENT\]\]/g, entente?.client?.name || 'Client inconnu')
            .replace(/\[\[DATE\]\]/g, formatDate(entente?.date_activite) || 'Date inconnue')
            .replace(/\[\[LIEU\]\]/g, formatAddress(entente?.lieu) || 'Lieu inconnu')
            .replace(/\[\[CONTACTS\.(\d+)\]\]/g, (_, index) => {
                const numIndex = parseInt(index);
                if (!entente?.contacts?.[numIndex]?.contact?.name) {
                    throw new Error(`Le contact ${numIndex} est introuvable.`);
                }
                return entente.contacts[numIndex].contact.name;
            })
            .replace(/\[\[ACTIVITES\.(\d+)\]\]/g, (_, index) => {
                const numIndex = parseInt(index);
                if (!entente?.activiteEntentes?.[numIndex]?.activite?.name) {
                    throw new Error(`L'activité ${numIndex} est introuvable.`);
                }
                return entente.activiteEntentes[numIndex].activite.name;
            });
    };

    const handleSendEmail = async () => {
        if (!emailRecipients[0] || !emailSubject || !emailMessage) {
            alert('Veuillez remplir tous les champs');
            return;
        }

        try {
            setEmailLoading(true);

            const emailAttachmentsData: EmailAttachment[] = [];
            for (const docType of emailAttachments) {
                const attachmentData = await createEmailAttachmentData(docType);
                emailAttachmentsData.push(attachmentData);
            }

            const formattedMessage = replaceEmailVariables(emailMessage);

            await envoyerEmail({
                recipients: emailRecipients,
                subject: emailSubject,
                message: formattedMessage,
                attachments: emailAttachmentsData,
            });
            setShowEmailModal(false);
            setEmailMessage('');
            setEmailSubject('');
            setEmailRecipients([]);
            bsAlert('Email envoyé', 'success');

            setEmailLoading(false);
        } catch (error) {
            console.error("Échec de l'envoi de l'email:", error);
            bsAlert("Échec de l'envoi de l'email", 'danger');
            setEmailLoading(false);
        }
    };

    return (
        <Container fluid className="p-4">
            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Envoyer un courriel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex">
                        <div className="flex-grow-1 pe-4">
                            <Form>
                                <Form.Group>
                                    <Form.Label>Modèle de courriel</Form.Label>
                                    <Form.Control as="select" onChange={(e) => handleTemplateChange(e.target.value)}>
                                        <option value="">Sélectionnez un modèle de courriel</option>
                                        {modelesEmails?.map((modele) => (
                                            <option key={modele.id} value={modele.id}>
                                                {modele.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>Destinataire</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Courriel du destinataire"
                                        value={emailRecipients[0] || ''}
                                        onChange={(e) => setEmailRecipients([e.target.value])}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>Titre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Titre du courriel"
                                        value={emailSubject}
                                        onChange={(e) => setEmailSubject(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Documents à inclure</Form.Label>
                                    <div className="d-flex flex-wrap" style={{ gap: '5px' }}>
                                        {documentTypes.map((doc) => (
                                            <Button
                                                key={doc}
                                                variant={emailAttachments.includes(doc) ? 'success' : 'outline-primary'}
                                                onClick={() =>
                                                    emailAttachments.includes(doc)
                                                        ? handleRemoveEmailAttachment(doc)
                                                        : handleAddEmailAttachment(doc)
                                                }
                                            >
                                                {emailAttachments.includes(doc)
                                                    ? `✓ ${beautifyType(doc)}`
                                                    : `+ ${beautifyType(doc)}`}
                                            </Button>
                                        ))}
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>Message</Form.Label>
                                    <RichTextEditor value={emailMessage} onChange={setEmailMessage} />
                                </Form.Group>
                            </Form>
                        </div>

                        <div className="border-start ml-3" style={{ minWidth: '320px' }}>
                            <LegendeCourriel />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={handleSendEmail} disabled={emailLoading}>
                        {emailLoading ? 'Envoi...' : 'Envoyer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Card className="mb-3 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>{entente?.name}</strong>
                    <div>
                        <Button variant="primary" onClick={() => navigate(`/dashboard/ententes/${id}`)}>
                            Modifier cette entente
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col>
                            <strong>No.entente:</strong> {entente?.id}
                        </Col>
                        <Col>
                            <strong>Status:</strong> {entente?.statut || 'Inactif'}
                        </Col>
                        <Col>
                            <strong>Date de début :</strong> {formatDate(entente?.date_activite)}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Row>
                <Col md={4}>
                    <DropdownItem title="Client">
                        <Row className="small">
                            <Col xs={5} className="font-weight-bold">
                                Client :
                            </Col>
                            <Col xs={7}>{entente?.client?.name || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Adresse de facturation :
                            </Col>
                            <Col xs={7}>{formatAddress(entente?.adresse_facturation) || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Contacts :
                            </Col>
                            <Col xs={7}>
                                {entente?.contacts?.length ?? 0 > 0
                                    ? entente?.contacts.map((c, index) => (
                                          <span key={index} className="d-inline-block">
                                              {c.contact.name}
                                              <ContactInfo contact={c.contact} />
                                              {index !== entente.contacts.length - 1 ? ', ' : ''}
                                          </span>
                                      ))
                                    : 'N/A'}
                            </Col>
                            <Col xs={12}>
                                <hr />
                            </Col>
                            {roleLabels.map((role, index) => (
                                <>
                                    <Col xs={5} className="font-weight-bold">
                                        {role} :
                                    </Col>
                                    <Col xs={7}>{getContactsByRole(index)}</Col>
                                </>
                            ))}
                        </Row>
                    </DropdownItem>

                    <DropdownItem title="Entente">
                        <Row className="small">
                            <Col xs={5} className="font-weight-bold">
                                Nom :
                            </Col>
                            <Col xs={7}>{entente?.name || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Date de début :
                            </Col>
                            <Col xs={7}>{formatDate(entente?.date_activite)}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Conditions :
                            </Col>
                            <Col xs={7}>{entente?.conditions[0]?.name || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Contexte :
                            </Col>
                            <Col xs={7}>{entente?.contexte || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Groupe d'âge :
                            </Col>
                            <Col xs={7}>{entente?.groupe_age || 'N/A'}</Col>

                            <Col xs={5} className="font-weight-bold">
                                Lieu de l'animation :
                            </Col>
                            <Col xs={7}>{formatAddress(entente?.lieu) || 'N/A'}</Col>
                        </Row>
                    </DropdownItem>

                    <DropdownItem title="Activités">
                        {entente?.activiteEntentes?.length ?? 0 > 0 ? (
                            entente?.activiteEntentes.map((activiteEntente, index) => (
                                <Row key={index} className="small mb-3">
                                    <Col xs={5} className="font-weight-bold">
                                        Nom :
                                    </Col>
                                    <Col xs={7}>{activiteEntente.activite?.name || 'N/A'}</Col>

                                    <Col xs={5} className="font-weight-bold">
                                        Thématique :
                                    </Col>
                                    <Col xs={7}>{activiteEntente.thematique?.name || 'N/A'}</Col>

                                    {index !== entente.activiteEntentes.length - 1 && (
                                        <Col xs={12}>
                                            <hr />
                                        </Col>
                                    )}
                                </Row>
                            ))
                        ) : (
                            <p className="small">Aucune activité</p>
                        )}
                    </DropdownItem>

                    <DropdownItem title="Notifications">
                        <p>0 Notifications envoyées</p>
                        <p>0 Notifications seront envoyées</p>
                        <p>0 Notifications désactivées</p>
                    </DropdownItem>
                </Col>

                <Col md={4}>
                    <DropdownItem title="Animations" />
                    <DropdownItem title="Produits" />
                    <DropdownItem title="Communication">
                        <Button variant="info" className="mr-2" onClick={() => setShowEmailModal(true)}>
                            Courriel
                        </Button>
                        <Button variant="info" className="mr-2">
                            Texter
                        </Button>
                        <Button variant="info">Enregistrer</Button>
                    </DropdownItem>
                    <DropdownItem title="Historique de l'entente" />
                    <DropdownItem title="Pièces jointes" />
                </Col>

                <Col md={4}>
                    <DropdownItem title="Pipeline" />
                    <DropdownItem title="Horaire" />
                    <DropdownItem title="Notes" />
                    <DropdownItem title="Document">
                        {documentTypes.map((docType, index) => (
                            <LinkContainer to={`/dashboard/ententes/${id}/doc/${docType}`} key={docType}>
                                <Button
                                    variant="success"
                                    className={index + 1 == documentTypes.length ? 'mb-2' : 'mr-2 mb-2'}
                                >
                                    {beautifyType(docType)}
                                </Button>
                            </LinkContainer>
                        ))}
                    </DropdownItem>
                    <DropdownItem title="Facturation" />
                </Col>
            </Row>
        </Container>
    );
};
const DropdownItem: React.FC<{
    title: string;
    defaultOpen?: boolean;
    children?: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <Card className="mb-2">
            <Card.Header
                className="d-flex justify-content-between align-items-center py-1"
                onClick={() => setIsOpen(!isOpen)}
                style={{ cursor: 'pointer' }}
            >
                <Button
                    variant="link"
                    className="text-left text-dark w-100 d-flex align-items-center"
                    style={{ textDecoration: 'none', fontWeight: 500, fontSize: '12px' }}
                >
                    {isOpen ? <BiChevronUp /> : <BiChevronDown />} {title}
                </Button>
            </Card.Header>
            <div className={`collapse ${isOpen ? 'show' : ''}`}>
                <Card.Body>{children || <p>Aucune donnée</p>}</Card.Body>
            </div>
        </Card>
    );
};

const renderPopover = (title: string, content: string) => (
    <Popover id={`popover-${title}`} className="shadow-sm">
        <Popover.Title as="h6">{title}</Popover.Title>
        <Popover.Content>{content}</Popover.Content>
    </Popover>
);

const ContactInfo: React.FC<{ contact: any }> = ({ contact }) => (
    <span className="ml-2">
        {contact.telephone && (
            <OverlayTrigger trigger="hover" placement="top" overlay={renderPopover('Téléphone', contact.telephone)}>
                <BiPhone className="ml-1 text-muted" />
            </OverlayTrigger>
        )}
        {contact.email && (
            <OverlayTrigger trigger="hover" placement="top" overlay={renderPopover('Courriel', contact.email)}>
                <BiAt className="ml-1 text-muted" />
            </OverlayTrigger>
        )}
        {contact.notes && (
            <OverlayTrigger trigger="hover" placement="top" overlay={renderPopover('Notes', contact.notes)}>
                <BiNote className="ml-1 text-muted" />
            </OverlayTrigger>
        )}
    </span>
);

export default MenuEntentes;
